import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@tonic/shared/components/ui';
import { MoreHorizontal } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

/**
 * @typedef {'pending' | 'in_transit' | 'completed' | 'cancelled'} TransferStatus
 */

/**
 * @typedef {Object} Location
 * @property {string} id - Location ID
 * @property {string} name - Location name
 * @property {string} code - Location code
 */

/**
 * @typedef {Object} Transfer
 * @property {string} id - Transfer ID
 * @property {string} reference - Transfer reference number
 * @property {Location} sourceLocation - Source location
 * @property {Location} destinationLocation - Destination location
 * @property {number} itemCount - Number of items in transfer
 * @property {TransferStatus} status - Transfer status
 * @property {string} createdAt - Creation timestamp
 */

/**
 * Get the background color class for a transfer status
 * @param {TransferStatus} status - The transfer status
 * @returns {string} The Tailwind CSS background color class
 */
const getStatusColor = (status) => {
  const colors = {
    pending: "bg-yellow-500",
    in_transit: "bg-blue-500",
    completed: "bg-green-500",
    cancelled: "bg-red-500",
  };
  return colors[status] || "bg-gray-500";
};

/**
 * Column definitions for the transfers table
 * @type {import('@tanstack/react-table').ColumnDef<Transfer>[]}
 */
export const columns = [
  {
    accessorKey: "reference",
    header: "Reference",
  },
  {
    accessorKey: "sourceLocationName",
    header: "From",
    cell: ({ row }) => {
      return (
        <div>
          <div className="font-medium">{row.original.sourceLocationName}</div>
          <div className="text-sm text-muted-foreground">{row.original.sourceLocationId}</div>
        </div>
      );
    },
  },
  {
    accessorKey: "destinationLocationName",
    header: "To",
    cell: ({ row }) => {
      return (
        <div>
          <div className="font-medium">{row.original.destinationLocationName}</div>
          <div className="text-sm text-muted-foreground">{row.original.destinationLocationId}</div>
        </div>
      );
    },
  },
  {
    accessorKey: "itemCount",
    header: "Items",
    cell: ({ row }) => {
      const count = row.original.itemCount;
      return (
        <Badge variant="outline" className="font-mono">
          {count}
        </Badge>
      );
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.original.status;
      return (
        <Badge className={getStatusColor(status)}>
          {status.replace("_", " ")}
        </Badge>
      );
    },
  },
  {
    accessorKey: "createdAt",
    header: "Created",
    cell: ({ row }) => {
      return formatDistanceToNow(new Date(row.original.createdAt), {
        addSuffix: true,
      });
    },
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      const transfer = row.original;
      const isPending = transfer.status === "pending";
      const isInTransit = transfer.status === "in_transit";

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => navigator.clipboard.writeText(transfer.id)}
            >
              Copy transfer ID
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem>View details</DropdownMenuItem>
            {isPending && (
              <>
                <DropdownMenuItem>Start Transfer</DropdownMenuItem>
                <DropdownMenuItem className="text-red-600">
                  Cancel Transfer
                </DropdownMenuItem>
              </>
            )}
            {isInTransit && (
              <DropdownMenuItem>Complete Transfer</DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
]; 
